import React, { useEffect, useMemo, useState } from 'react';
import { FaDownload, FaSearch, FaBookOpen } from 'react-icons/fa';
import toast from 'react-hot-toast';

const ITEMS_PER_PAGE = 10;

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

function AdminWorkDiaryDashboard({ apiUrl, adminFetch, apiBasePath = 'api/admin' }) {
  const [diaries, setDiaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedDiary, setSelectedDiary] = useState(null);
  const totalPages = useMemo(() => Math.ceil(total / ITEMS_PER_PAGE) || 1, [total]);
  const workDiariesBase = `${apiUrl}/${apiBasePath}/work-diaries`;
  const workDiaryEntriesBase = `${apiUrl}/${apiBasePath}/work-diary-entries`;
  const fetchIdRef = React.useRef(0);

  useEffect(() => {
    const id = ++fetchIdRef.current;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page,
          limit: ITEMS_PER_PAGE,
          search: search.trim()
        });
        const res = await adminFetch(`${workDiariesBase}?${params.toString()}`);
        let data;
        try {
          data = await res.json();
        } catch (_) {
          if (!cancelled && id === fetchIdRef.current) {
            toast.error(res.status === 401 ? 'Please log in again' : 'Failed to load work diaries');
          }
          return;
        }
        if (cancelled || id !== fetchIdRef.current) return;
        if (!res.ok) {
          const errMsg = data?.error || data?.message || (res.status === 401 ? 'Please log in again' : 'Failed to load work diaries');
          toast.error(errMsg);
          return;
        }
        if (data.success) {
          setDiaries(data.diaries || []);
          setTotal(data.total || 0);
        } else {
          toast.error(data.error || data.message || 'Failed to load work diaries');
        }
      } catch (err) {
        if (cancelled || id !== fetchIdRef.current) return;
        const msg = err?.message || 'Failed to load work diaries';
        toast.error(msg === 'Not authenticated' ? 'Please log in again' : msg);
        console.error('Work diaries fetch error:', err);
      } finally {
        if (id === fetchIdRef.current) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleExport = async (diaryId, candidateName) => {
    try {
      const res = await adminFetch(`${workDiariesBase}/${diaryId}/export`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to export work diary');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (candidateName || 'work-diary').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
      link.download = `${safeName}-work-diary.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Work diary exported');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to export');
    }
  };

  const openDiary = async (diary) => {
    try {
      const res = await adminFetch(`${workDiariesBase}/${diary.id}`);
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to load diary');
      }
      setSelectedDiary(data.diary);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to fetch diary');
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: '#22c55e', letterSpacing: 0.5 }}>Work Diary Submissions</h2>
        <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <FaSearch style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search candidate name, email or mobile"
              className="gogig-input"
              style={{ paddingLeft: 40, minWidth: 280 }}
            />
          </div>
          <button type="submit" className="gogig-card-btn">Search</button>
        </form>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16,
        marginBottom: 24
      }}>
        <div style={{ background: '#ecfdf5', border: '1px solid #bbf7d0', borderRadius: 16, padding: 20 }}>
          <div style={{ fontSize: 12, color: '#16a34a', textTransform: 'uppercase', fontWeight: 700 }}>Total Diaries</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: '#16a34a' }}>{total}</div>
          <div style={{ fontSize: 13, color: '#166534' }}>Active submissions recorded</div>
        </div>
        <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 16, padding: 20 }}>
          <div style={{ fontSize: 12, color: '#b45309', textTransform: 'uppercase', fontWeight: 700 }}>Due Updates</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: '#92400e' }}>
            {diaries.filter(d => d.next_due_date && new Date(d.next_due_date) < new Date()).length}
          </div>
          <div style={{ fontSize: 13, color: '#92400e' }}>Candidates overdue for weekly entry</div>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', boxShadow: '0 2px 16px rgba(34,197,94,0.08)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'left', color: '#1f2937' }}>
                <th style={{ padding: '14px 16px', fontWeight: 700 }}>Candidate</th>
                <th style={{ padding: '14px 16px', fontWeight: 700 }}>Trade</th>
                <th style={{ padding: '14px 16px', fontWeight: 700 }}>Entries</th>
                <th style={{ padding: '14px 16px', fontWeight: 700 }}>Last Entry</th>
                <th style={{ padding: '14px 16px', fontWeight: 700 }}>Next Due</th>
                <th style={{ padding: '14px 16px', fontWeight: 700 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>Loading work diaries...</td>
                </tr>
              ) : diaries.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>No work diaries found.</td>
                </tr>
              ) : (
                diaries.map((diary) => (
                  <tr key={diary.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 700, color: '#111827' }}>{diary.Candidate?.name || 'Unknown'}</div>
                      <div style={{ fontSize: 13, color: '#6b7280' }}>{diary.Candidate?.email}</div>
                      <div style={{ fontSize: 13, color: '#6b7280' }}>{diary.Candidate?.mobile}</div>
                    </td>
                    <td style={{ padding: '14px 16px', color: '#1f2937' }}>{diary.trade || '-'}</td>
                    <td style={{ padding: '14px 16px', color: '#1f2937', fontWeight: 600 }}>{diary.entry_count || 0}</td>
                    <td style={{ padding: '14px 16px', color: '#1f2937' }}>{formatDate(diary.last_entry_date)}</td>
                    <td style={{ padding: '14px 16px', color: diary.next_due_date ? '#b91c1c' : '#1f2937' }}>
                      {diary.next_due_date ? formatDate(diary.next_due_date) : 'Not scheduled'}
                    </td>
                    <td style={{ padding: '14px 16px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        className="gogig-card-btn-outline"
                        onClick={() => openDiary(diary)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        <FaBookOpen /> View
                      </button>
                      <button
                        className="gogig-card-btn"
                        onClick={() => handleExport(diary.id, diary.Candidate?.name)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        <FaDownload /> Export
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid #e5e7eb', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ color: '#6b7280', fontSize: 13 }}>
            Page {page} of {totalPages}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="gogig-card-btn-outline"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <button
              className="gogig-card-btn-outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {selectedDiary && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 20,
            padding: 28,
            width: '95%',
            maxWidth: 900,
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 24px 80px rgba(0,0,0,0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 24, color: '#16a34a' }}>
                  {selectedDiary.Candidate?.name || 'Candidate'} – Work Diary
                </h3>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  {selectedDiary.establishment_name} · {selectedDiary.trade}
                </div>
              </div>
              <button
                onClick={() => setSelectedDiary(null)}
                style={{ fontSize: 28, background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
              <div style={{ background: '#f0fdf4', borderRadius: 12, padding: 16, border: '1px solid #bbf7d0' }}>
                <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>Apprenticeship Code</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#14532d' }}>{selectedDiary.apprenticeship_code || '-'}</div>
              </div>
              <div style={{ background: '#f0fdf4', borderRadius: 12, padding: 16, border: '1px solid #bbf7d0' }}>
                <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>Establishment</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#14532d' }}>{selectedDiary.establishment_name || '-'}</div>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                <thead>
                  <tr style={{ background: '#ecfdf5', color: '#065f46' }}>
                    <th style={{ padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #bbf7d0' }}>Serial</th>
                    <th style={{ padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #bbf7d0' }}>Week</th>
                    <th style={{ padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #bbf7d0' }}>Job / Work Done</th>
                    <th style={{ padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #bbf7d0' }}>Supervisor</th>
                    <th style={{ padding: '12px 10px', textAlign: 'left', borderBottom: '1px solid #bbf7d0' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedDiary.entries || []).length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No entries recorded yet.</td>
                    </tr>
                  ) : (
                    selectedDiary.entries.map((entry) => (
                      <tr key={entry.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '12px 10px', fontWeight: 600 }}>{entry.serial_number}</td>
                        <td style={{ padding: '12px 10px' }}>{formatDate(entry.week_start)} – {formatDate(entry.week_end)}</td>
                        <td style={{ padding: '12px 10px', whiteSpace: 'pre-wrap' }}>{entry.job_work_done}</td>
                        <td style={{ padding: '12px 10px' }}>{entry.supervisor_signature || '-'}</td>
                        <td style={{ padding: '12px 10px' }}>
                          {!entry.supervisor_signature && (
                            <button
                              className="gogig-card-btn-outline"
                              onClick={async () => {
                                const name = window.prompt('Enter supervisor/admin name to mark this entry as signed:');
                                if (!name) return;
                                try {
                                  const res = await adminFetch(`${workDiaryEntriesBase}/${entry.id}/sign`, {
                                    method: 'PATCH',
                                    headers: {
                                      'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({ supervisor_signature: name })
                                  });
                                  const data = await res.json();
                                  if (!data.success) {
                                    throw new Error(data.message || 'Failed to sign entry');
                                  }
                                  toast.success('Entry marked as signed');
                                  // Reload diary
                                  await openDiary(selectedDiary);
                                } catch (err) {
                                  console.error(err);
                                  toast.error(err.message || 'Failed to sign entry');
                                }
                              }}
                            >
                              Mark Signed (Admin)
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 16, fontSize: 12, color: '#6b7280' }}>
              This view reflects submitted weekly updates. Use the Export button to download a signed copy.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

AdminWorkDiaryDashboard.defaultProps = {
  apiUrl: '',
  adminFetch: async () => {
    throw new Error('adminFetch not provided');
  }
};

export default AdminWorkDiaryDashboard;



